import { auth, db } from "../firebase";
import {
  createUserWithEmailAndPassword,
  signOut,
  getAuth,
} from "firebase/auth";
import { initializeApp, getApps } from "firebase/app";
import { doc, setDoc, getFirestore } from "firebase/firestore";

// Import firebase config to create temporary app
const firebaseConfig = {
  apiKey: "AIzaSyCxXbRnQulAx0oR9LBexbesGz3IEamz6jA",
  authDomain: "burimi-drivingschool.firebaseapp.com",
  projectId: "burimi-drivingschool",
  storageBucket: "burimi-drivingschool.firebasestorage.app",
  messagingSenderId: "1082841568170",
  appId: "1:1082841568170:web:472de22f6132cd050251d3",
  measurementId: "G-2EL6T6QRCR",
};

/**
 * Create a Firebase Auth account and user document for instructors or candidates
 * IMPORTANT: This function uses a temporary Firebase app instance to create the user
 * without affecting the current admin session
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} role - User role ('instructor' or 'candidate')
 * @param {object} userData - Additional user data (firstName, lastName, phoneNumber, etc.)
 * @param {string} entityId - The ID of the instructor or candidate document in Firestore
 * @param {string} adminEmail - Admin's email (not used, kept for backwards compatibility)
 * @param {string} adminPassword - Admin's password (not used, kept for backwards compatibility)
 * @returns {Promise<{success: boolean, user?: object, error?: string}>}
 */
export const createUserAccount = async (
  email,
  password,
  role,
  userData,
  entityId = null,
  adminEmail = null,
  adminPassword = null
) => {
  // Store reference to current user (we'll verify it's still the same after)
  const currentUserBefore = auth.currentUser;
  const currentUserUid = currentUserBefore?.uid;

  try {
    // Validate role
    if (role !== "instructor" && role !== "candidate" && role !== "admin") {
      return {
        success: false,
        error: "Invalid role. Must be 'instructor', 'candidate', or 'admin'",
      };
    }

    // Validate email format
    if (!email || !email.includes("@")) {
      return {
        success: false,
        error: "Valid email is required",
      };
    }

    // Validate password
    if (!password || password.length < 6) {
      return {
        success: false,
        error: "Password must be at least 6 characters",
      };
    }

    // Create a temporary Firebase app instance to create the user
    // This prevents the new user from signing in to the main app
    const tempAppName = `temp_${Date.now()}`;
    let tempApp;
    let tempAuth;

    try {
      // Create temporary app with same config but different name
      tempApp = initializeApp(firebaseConfig, tempAppName);
      tempAuth = getAuth(tempApp);

      // Create user with email and password using temporary auth instance
      // This will sign in to the temporary app, not the main app
      const userCredential = await createUserWithEmailAndPassword(
        tempAuth,
        email,
        password
      );
      const user = userCredential.user;
      const newUserUid = user.uid;

      // Create user document in Firestore users collection using main db instance
      await setDoc(doc(db, "users", user.uid), {
        uid: user.uid,
        email: email,
        role: role,
        firstName: userData.firstName || "",
        lastName: userData.lastName || "",
        phoneNumber: userData.phoneNumber || null,
        entityId: entityId, // Link to instructor or candidate document
        createdAt: new Date().toISOString(),
      });

      // Sign out from temporary app
      await signOut(tempAuth);

      // Delete temporary app to clean up
      await tempApp.delete();

      // Verify that the current user in the main app is still the admin
      // (should be unchanged since we used a temporary app)
      if (currentUserUid && auth.currentUser?.uid !== currentUserUid) {
        console.warn(
          "Warning: Current user changed after creating new user account"
        );
      }

      return {
        success: true,
        user: { uid: newUserUid, email: email },
        uid: newUserUid,
        message: `${role} user account created successfully!`,
      };
    } catch (tempError) {
      // Clean up temporary app if it was created
      if (tempApp) {
        try {
          await tempApp.delete();
        } catch (deleteError) {
          console.error("Error deleting temporary app:", deleteError);
        }
      }
      throw tempError;
    }
  } catch (error) {
    console.error(`Error creating ${role} user account:`, error);

    // Handle specific Firebase errors
    let errorMessage = error.message;
    if (error.code === "auth/email-already-in-use") {
      errorMessage = "An account with this email already exists";
    } else if (error.code === "auth/invalid-email") {
      errorMessage = "Invalid email address";
    } else if (error.code === "auth/weak-password") {
      errorMessage = "Password is too weak";
    }

    return {
      success: false,
      error: errorMessage,
    };
  }
};

/**
 * Generate email from username if email is not provided
 * @param {string} username - Username
 * @param {string} email - Existing email (optional)
 * @returns {string} - Generated or existing email
 */
export const generateEmail = (username, email = null) => {
  if (email && email.includes("@")) {
    return email;
  }

  if (username) {
    // Convert username to email format
    return `${username}@autoshkollaburimi.com`;
  }

  return null;
};
